'use client'

import {
  FilterIcon,
  DetailText,
  LeftCaretIcon,
  BodyText,
  HeadlineText,
  ArrowRightIcon
} from 'design-configuration/components'
import { useState, useRef, useEffect } from 'react'
import { AnimatePresence, LayoutGroup, motion, Transition } from 'motion/react'

import { Button } from '@components/button'
import { Dropdown } from '@components/dropdown'
import { PaginationDirection } from '@components/pagination-button'
import { FilterPanel, FilterPanelAnimation } from './filter-panel'
import { trackButtonClicked, TrackedButtonNames, TrackedSelectionNames, trackSelectionMade } from '@utils/analytics'
import { cn } from '@/lib/utils'
import { LoadingSpinner } from '@components/loading-spinner'

// For Mixpanel Reasons
export enum GridType {
  Collections = 'Collections',
  Tokens = 'Tokens',
  Articles = 'Articles',
  Artists = 'Artists',
  Exhibitions = 'Exhibitions',
  SecondaryMarketplace = 'Secondary Marketplace'
}

export const gridItemTransition: Transition = { duration: 0.3, ease: 'easeInOut' }

type DefaultGridProps<GridItemData extends Record<string, unknown>, SortOrder extends string> = {
  name: string
  /** Grid type used for analytics */
  gridType: GridType
  /** Grid name used for analytics */
  gridName?: string
  /** Current sort order */
  sortOrder: SortOrder
  /** List of possible sort options */
  sortOptions: SortOrder[]
  /** Callback for changing the sort order */
  onSortOrderChange: (newSortOrder: SortOrder) => void
  /** Callback for changing the page number */
  onPageNumberChange: (pageNumber: number) => void
  /** Current page number */
  pageNumber: number
  /** Total number of pages for current data */
  totalPages: number
  /** Total number of items in the grid */
  totalItems: number
  /** Number of currently active filters */
  numberOfActiveFilters: number
  /** Component for the filter panel */
  filterLayout?: React.ReactElement
  /** Animation variant for the filter panel */
  filterAnimation?: FilterPanelAnimation
  /** Callback for clearing filters */
  onClearFilters: () => void
  /** Data for the grid items */
  gridItems: Array<GridItemData>
  /** Render function for grid items, called with the item data */
  gridItemComponent: (item: GridItemData) => React.ReactElement
  /** Grid item key function */
  gridItemKey?: (item: GridItemData) => string
  /** Classnames applied to inner grid element when data is present */
  gridClassNameOverride?: string
  /** Classnames applied to header element */
  headerClassNameOverride?: string
  /** Used for filtering notice text */
  itemNames?: string
  /** Is the data for the grid currently fetching */
  fetching?: boolean
  /** Extra UI shown next to the sort dropdown */
  additionalControls?: React.ReactElement
  /** Search controls that will expand to fill available horizontal space */
  searchControls?: React.ReactElement
  /** Whether the filter panel should be open by default */
  filterDefaultOpen?: boolean
  /** Classnames applied to the grid container */
  className?: string
  /** Optional custom empty grid component to use when no items are found */
  customEmptyGrid?: React.ReactElement

  /** Optional custom filter button replacement */
  filterButtonReplacement?: React.ReactElement
  /** Optional custom title text component */
  titleTextComponent?: React.ReactElement
}

export const Grid = <GridItemData extends Record<string, unknown>, SortOrder extends string>({
  name,
  gridType,
  sortOrder,
  sortOptions,
  onSortOrderChange: setSortOrder,
  onPageNumberChange: setPageNumber,
  pageNumber,
  totalPages,
  totalItems,
  numberOfActiveFilters,
  gridName,
  filterLayout,
  filterAnimation,
  onClearFilters: clearFilters,
  gridItems,
  gridItemComponent,
  gridItemKey,
  gridClassNameOverride,
  headerClassNameOverride,
  itemNames = 'artworks',
  fetching = false,
  additionalControls,
  searchControls,
  filterDefaultOpen = false,
  className,
  customEmptyGrid,
  filterButtonReplacement,
  titleTextComponent
}: DefaultGridProps<GridItemData, SortOrder>): React.ReactElement => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [openFilter, setOpenFilter] = useState(false)
  const [fetched, setfetched] = useState(false)

  // Do not auto-open filter panel on mobile
  useEffect(() => {
    if (filterDefaultOpen) {
      const mediaQuery = window.matchMedia('(min-width: 768px)')
      setOpenFilter(mediaQuery.matches)

      const handler = (e: MediaQueryListEvent) => {
        setOpenFilter(e.matches)
      }

      mediaQuery.addEventListener('change', handler)
      return () => mediaQuery.removeEventListener('change', handler)
    }
  }, [filterDefaultOpen])

  useEffect(() => {
    if (!fetching) {
      setfetched(true)
    }
  }, [fetching])

  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const changePage = (direction: PaginationDirection) => {
    if (direction === PaginationDirection.Right) {
      if (pageNumber === totalPages) {
        setPageNumber(1)
      } else {
        setPageNumber(pageNumber + 1)
      }
      trackButtonClicked(TrackedButtonNames.paginateRight, {
        customProperties: {
          Grid: gridType
        }
      })
    } else {
      if (pageNumber === 1) {
        setPageNumber(totalPages)
      } else {
        setPageNumber(pageNumber - 1)
      }
      trackButtonClicked(TrackedButtonNames.paginateLeft, {
        customProperties: {
          Grid: gridType
        }
      })
    }

    scrollToTop()
  }

  const clearFiltersClick = () => {
    clearFilters()
    trackButtonClicked(TrackedButtonNames.clearFilters, {
      customProperties: {
        Grid: gridType
      }
    })
  }

  const activelyFiltering = numberOfActiveFilters !== 0

  const filteringDetails = (
    <div className="gap-x-sm flex w-full items-center justify-start">
      <motion.div
        layout="position"
        className="text-on-surface gap-x-sm hidden items-center lg:flex"
        transition={{ duration: 0.15 }}
      >
        {titleTextComponent ? (
          <>
            {titleTextComponent}
            <div className="h-sm bg-on-surface-variant w-[1px]" />
          </>
        ) : null}

        {fetched ? (
          <BodyText>
            {`${activelyFiltering ? `Filtering ${itemNames}` : `All ${itemNames}`} (${totalItems} result${
              totalItems !== 1 ? 's' : ''
            })`}
          </BodyText>
        ) : null}
      </motion.div>
      {activelyFiltering ? (
        <motion.button
          layout="position"
          className="gap-x-xs text-on-surface-variant rounded-xs hidden items-center underline lg:flex"
          onClick={clearFiltersClick}
          transition={{ duration: 0.15 }}
        >
          <BodyText>Remove all filters</BodyText>
        </motion.button>
      ) : null}
    </div>
  )

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      className={cn('px-md pb-md relative w-full', className)}
      ref={scrollRef}
    >
      <div className={cn('py-md gap-y-sm flex w-full flex-col', headerClassNameOverride)}>
        <div className={'flex w-full items-center justify-between'}>
          <div className="gap-x-sm flex items-center justify-start">
            <LayoutGroup>
              {filterButtonReplacement ? (
                filterButtonReplacement
              ) : (
                <motion.div layout="position" className={cn('w-auto', filterLayout ? 'block' : 'hidden')}>
                  <Button
                    colorTheme="outline"
                    onClick={() => {
                      trackButtonClicked(TrackedButtonNames.toggleFilterPanel, {
                        customProperties: {
                          Grid: gridType,
                          panelOpen: !openFilter
                        }
                      })
                      setOpenFilter(!openFilter)
                    }}
                    className={cn('px-xs sm:px-sm w-auto')}
                  >
                    {openFilter ? (
                      <motion.div
                        key="Hide Filter Button"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="gap-x-xs flex items-center justify-center"
                      >
                        <LeftCaretIcon className="w-sm h-sm fill-on-primary-inverted" />
                        <BodyText className="whitespace-nowrap">Hide filters</BodyText>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="Filter Button"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="gap-x-xs flex items-center justify-center"
                      >
                        <FilterIcon className="w-sm h-sm fill-on-primary-inverted" />
                        <BodyText className="hidden lg:block">Filter</BodyText>
                      </motion.div>
                    )}
                  </Button>
                </motion.div>
              )}
              {!searchControls ? filteringDetails : null}
            </LayoutGroup>
          </div>
          {searchControls ? <div className="px-md flex-1">{searchControls}</div> : null}
          <div className="gap-x-xs flex flex-shrink-0 items-center">
            {additionalControls}
            {sortOptions && sortOptions.length > 0 ? (
              <Dropdown
                options={sortOptions}
                selected={sortOrder}
                setSelected={(option: string) => {
                  trackSelectionMade(TrackedSelectionNames.sortOrder, {
                    selection: option,
                    options: sortOptions,
                    customProperties: {
                      Grid: gridType
                    }
                  })
                  setSortOrder(option as SortOrder)
                }}
              />
            ) : null}
          </div>
        </div>
        {searchControls ? filteringDetails : null}
      </div>
      <AnimatePresence>
        {fetched ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="relative flex h-full w-full"
          >
            <LayoutGroup>
              {openFilter && filterLayout ? (
                <FilterPanel
                  filterLayout={filterLayout}
                  filterOpen={openFilter}
                  itemCount={totalItems}
                  numActiveFilters={numberOfActiveFilters}
                  clearFilter={clearFiltersClick}
                  onFilterClose={() => setOpenFilter(false)}
                  animationVariant={filterAnimation}
                />
              ) : null}
              {totalItems === 0 ? (
                customEmptyGrid || <EmptyGrid clearFiltersClick={clearFiltersClick} />
              ) : (
                <motion.div className="flex h-full w-full flex-col">
                  <AnimatePresence initial={false} mode="popLayout">
                    {gridName !== '' ? (
                      <motion.div
                        className={cn(
                          `gap-md relative grid h-fit grid-flow-row auto-rows-min`,
                          gridClassNameOverride
                            ? gridClassNameOverride
                            : openFilter
                              ? 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                              : 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-4'
                        )}
                        key={`${gridName ?? name}-grid`}
                        id={`${gridName ?? name}-grid`}
                        initial={{ opacity: 0 }}
                        animate={{
                          opacity: fetching ? [0.6, 0.3, 0.6] : 1
                        }}
                        exit={{ opacity: 0, transition: gridItemTransition }}
                        transition={{
                          ...gridItemTransition,
                          repeat: fetching ? Infinity : 0,
                          duration: fetching ? 1.8 : 0.3
                        }}
                      >
                        <AnimatePresence mode="popLayout">
                          {gridItems?.map((item, index) => {
                            const getKey = () => {
                              if (gridItemKey) {
                                return gridItemKey(item)
                              } else if ('id' in item && typeof item.id === 'string') {
                                return item.id
                              } else {
                                return index
                              }
                            }

                            return (
                              <motion.div
                                key={getKey()}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={gridItemTransition}
                                className={fetching ? 'pointer-events-none' : ''}
                              >
                                {gridItemComponent(item)}
                              </motion.div>
                            )
                          })}
                        </AnimatePresence>
                      </motion.div>
                    ) : (
                      <div className="min-h-[85vh]" />
                    )}
                  </AnimatePresence>
                  {totalPages > 1 ? (
                    <motion.div layout className="py-2xl gap-x-sm flex items-center justify-center">
                      <Button
                        colorTheme="outline"
                        onClick={() => changePage(PaginationDirection.Left)}
                        iconLeft={<ArrowRightIcon className="w-sm h-sm fill-on-surface rotate-180" />}
                        className="px-xs"
                      ></Button>

                      <motion.div className="gap-x-xs text-on-surface flex font-medium" key={'page-details'}>
                        <DetailText>{`${pageNumber}`}</DetailText>
                        <DetailText>/</DetailText>
                        <DetailText>{`${totalPages}`}</DetailText>
                      </motion.div>

                      <Button
                        colorTheme="outline"
                        onClick={() => changePage(PaginationDirection.Right)}
                        iconRight={<ArrowRightIcon className="w-sm h-sm fill-on-surface" />}
                        className="px-xs"
                      ></Button>
                    </motion.div>
                  ) : null}
                </motion.div>
              )}
            </LayoutGroup>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="gap-x-sm my-[120px] flex h-full w-full items-center justify-center"
          >
            <LoadingSpinner className="h-lg w-lg" color="rgb(var(--on-surface-variant))" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

const EmptyGrid = ({ clearFiltersClick }: { clearFiltersClick: () => void }): React.ReactElement => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="gap-y-sm my-[80px] flex h-full w-full flex-col items-center justify-center"
    >
      <HeadlineText className="font-medium">No items found</HeadlineText>
      <BodyText>Try changing your filters or starting over</BodyText>
      <Button colorTheme={'secondary'} onClick={clearFiltersClick} className="mt-xs">
        Reset all filters
      </Button>
    </motion.div>
  )
}

type GridSkeletonProps = {
  /** Number of placeholder items to display */
  numberOfItems?: number
  /** Aspect ratio for grid items (width/height) */
  gridItemAspectRatio?: number
  /** Classnames applied to inner grid element */
  gridClassNameOverride?: string
}

export const GridSkeleton = ({
  numberOfItems = 12,
  gridItemAspectRatio = 1,
  gridClassNameOverride
}: GridSkeletonProps): React.ReactElement => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="px-md pb-md relative w-full"
    >
      {/* Skeleton for top controls */}
      <div className="py-md flex w-full items-center justify-between">
        <div className="gap-x-sm flex w-full items-center justify-start">
          <div className="bg-outline-variant h-[40px] w-[120px] animate-pulse rounded-md"></div>
          <div className="bg-outline-variant ml-sm hidden h-[24px] w-[180px] animate-pulse rounded-md md:block"></div>
        </div>
        <div className="bg-outline-variant h-[40px] w-[100px] animate-pulse rounded-md"></div>
      </div>

      {/* Skeleton grid */}
      <div className="relative flex h-full w-full">
        <div className="flex h-full w-full flex-col">
          <div
            className={cn(
              `gap-x-md gap-y-md relative grid h-fit grid-flow-row`,
              gridClassNameOverride ? gridClassNameOverride : 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-4'
            )}
          >
            {Array.from({ length: numberOfItems }).map((_, index) => (
              <div
                key={index}
                className="bg-outline-variant animate-pulse rounded-md"
                style={{ aspectRatio: gridItemAspectRatio }}
              ></div>
            ))}
          </div>

          {/* Skeleton pagination */}
          <div className="py-2xl gap-x-sm flex items-center justify-center">
            <div className="bg-outline-variant h-[40px] w-[40px] animate-pulse rounded-md"></div>
            <div className="bg-outline-variant mx-sm h-[24px] w-[60px] animate-pulse rounded-md"></div>
            <div className="bg-outline-variant h-[40px] w-[40px] animate-pulse rounded-md"></div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
